import type { Maybe } from '@adornis/base/utilTypes.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { saveWorkshopSet } from '@adornis/buchungsheld-db/api/workshop-set.js';
import { Category } from '@adornis/buchungsheld-db/entities/category.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { TextMultipleLangItem } from '@adornis/buchungsheld-db/entities/TextMultipleLangItem.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { WorkshopSet } from '@adornis/buchungsheld-db/entities/WorkshopSet.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import '@adornis/buchungsheld-theme/components/tr-currency-input.js';
import '@adornis/buchungsheld-theme/components/tr-formfield-wrapper.js';
import '@adornis/buchungsheld-theme/components/tr-input.js';
import '@adornis/buchungsheld-theme/components/tr-tinymce.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import '@adornis/chemistry/elements/components/x-card.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { buildFullOptions, type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/forms/x-checkbox.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { webcomponent } from '@adornis/functional-lit/webcomponent.js';
import '@adornis/popover/x-dropdown-multiselection.js';
import '@adornis/popover/x-tooltip';
import { goTo } from '@adornis/router/client/open-href.js';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../../../../components/bh-admin-product-entry-requirements-overview.js';
import '../../../../components/bh-admin-product-waitinglist-priority-overview.js';
import '../../../../components/bh-admin-teaching-overview.js';
import '../../../../components/bh-category-picker.js';
import '../../../../components/bh-product-certificate-picker.js';
import { BHFormController } from '../../../../controller/BHFormController.js';
import { BHPromptWorkshopSelectOrAdd } from '../../../../prompts/bh-prompt-workshop-select-or-add.js';
import { useContextEntity } from '../../../../util.js';

export const workshopSetModify = webcomponent('bh-page-admin-einrichtung-workshop-set-modify', () => {
  const [institution] = useContextEntity<typeof Institution>(Institution)({ _id: 1 });
  const [workshopSet] = useContextEntity(WorkshopSet)({
    _id: 1,
    name: 1,
    institutionID: 1,
    translatedDescriptionHTML: TextMultipleLangItem.allFields,
    categoryID: 1,
    category: Category.allFields,
    timeframeID: 1,
    timeframe: Timeframe.allFields,
    workshops: { _id: 1, name: 1, institutionID: 1 },
  });

  const { value: timeframes } = useQuery(
    () => Timeframe.getTimeframesByInstitution(institution?._id ?? '')(Timeframe.allFields),
    [institution?._id],
  );

  if (!institution || !workshopSet || !timeframes) return html`<x-loading-state></x-loading-state>`;
  const mode = workshopSet.isFromNetwork ? 'edit' : 'add';

  return html`
    <bh-page-admin-einrichtung-workshops-form
      .institution=${institution}
      .workshopSet=${workshopSet}
      .timeframes=${timeframes}
      .mode=${mode}
    ></bh-page-admin-einrichtung-workshops-form>
  `;
});

export const formDataViewSection = stateful<{
  title: string;
  onAddButtonClick: () => Promise<void> | void;
  addButtonText: string;
  data: any[];
  dataViewOptions: DataViewOptions<any>;
}>(p => {
  return html`
    <x-flex space="sm">
      <x-flex horizontal crossaxis-center space-between space="md">
        <x-h3>${p.title}</x-h3>
        <x-button mode="text" icon="add" @click=${() => p.onAddButtonClick()}> ${p.addButtonText} </x-button>
      </x-flex>
      <tr-data-view-table
        .loading=${false}
        .data=${p.data}
        .fullOptions=${buildFullOptions(p.dataViewOptions).dataViewOptions}
      ></tr-data-view-table>
    </x-flex>
  `;
});

@customElement('bh-page-admin-einrichtung-workshops-form')
export class BHPageAdminEinrichtungWorkshopsForm extends BuchungsheldLitElement {
  @property({ attribute: false })
  institution!: Institution;

  @property({ attribute: false })
  timeframes!: [Timeframe];

  @property({ attribute: false })
  workshopSet = new RXController<Maybe<WorkshopSet>>(this, null);

  @property({ attribute: false })
  mode!: 'add' | 'edit';

  form = new BHFormController(this, this.workshopSet.observable);

  protected override render() {
    const institutionID = this.institution._id;
    const f = this.form;
    const doc = this.form.document;

    if (!doc) return html`<x-loading-state></x-loading-state>`;

    // TODO: make a list out of that
    const formhtml = html`
      <x-input type="text" placeholder="Name" ${f.field('name')}></x-input>

      <bh-admin-multi-lang-text
        label="${this._translation.translate('field', 'description')}*"
        ${f.field('translatedDescriptionHTML')}
      ></bh-admin-multi-lang-text>

      <bh-category-picker
        institution-id=${institutionID}
        ${f.field('category')}
        @value-picked=${e => (doc.categoryID = e.detail.value?._id)}
      ></bh-category-picker>

      <!-- timeframe -->
      ${this.timeframeDropdown(f.field('timeframeID'))}

      <!-- workshops -->
      ${formDataViewSection({
        title: this._translation.translate('workshop', 'plural'),
        onAddButtonClick: () => this.addWorkshop(),
        addButtonText: this._translation.translate('workshop', 'add'),
        data: doc.workshops ?? [],
        dataViewOptions: {
          class: Workshop,
          customColumns: [
            { fieldName: 'name', columnWidth: '1fr' },
            {
              customID: '_options',
              columnWidth: 'min-content',
              renderItem: this.renderWorkshopListEntryOptions.bind(this),
            },
          ],
        },
      })}
    `;

    this.exposeAction('save', {
      icon: 'save',
      text: this._translation.translate('save'),
      callback: () => this.save(),
    });

    if (this.mode === 'edit') {
      this.exposeAction('delete', {
        icon: 'delete',
        color: 'error',
        callback: () => this.delete(),
      });
    }

    return html` <x-flex space="xl"> ${formhtml} </x-flex> `;
  }

  // helper

  async save() {
    const e = this.form.document;
    if (!e) return XSnackbar.show('Programmierfehler');
    const workshopSet = await this.form.validate({
      recurse: false,
      aggregateErrors: true,
      validateResolved: true,
    });

    if (!workshopSet) return;
    const workshopIDs = workshopSet.workshops?.map(w => w._id) ?? [];
    const newWorkshopSet = await XDialog.waitFor(saveWorkshopSet(workshopSet, workshopIDs)({})).catch(this.e);
    await XSnackbar.show(this._translation.translate('saved') + '!');
    if (this.mode === 'add' && newWorkshopSet?._id) {
      // go from add to edit page
      goTo(`/admin/institution-details/${this.institution._id}/workshop-sets/modify/${newWorkshopSet._id}`);
    }
  }

  async delete() {
    const workshopSet = this.form.document;
    if (!workshopSet) return XSnackbar.show('Programmierfehler');
    const deleteWorkshopSet = workshopSet.remove();
    await XDialog.waitFor(deleteWorkshopSet).catch(this.e);
    await XSnackbar.show(this._translation.translate('deleted') + '!');
    const institutionID = this.institution._id;
    goTo(`/admin/institution-details/${institutionID}/workshop-sets`);
  }

  async addWorkshop() {
    const doc = this.form.document;
    if (!doc) return;
    const workshop = await BHPromptWorkshopSelectOrAdd.selectOrAdd(this.institution._id);
    if (workshop) {
      if (!Array.isArray(doc.workshops)) doc.workshops = [];
      doc.workshops.push(workshop);
    }
    this.requestUpdate();
  }

  editWorkshop(workshop) {
    goTo(`/admin/institution-details/${workshop.institutionID}/workshops/detail/${workshop._id}?mode=data`);
  }

  removeWorkshop(workshop) {
    const doc = this.form.document;
    if (!doc) return;
    doc.workshops = doc.workshops.filter(w => w._id !== workshop._id);
    this.requestUpdate();
  }

  renderWorkshopListEntryOptions(workshop) {
    return html`
      <x-flex horizontal space="xs">
        <tr-button-small icon="edit" @click=${() => this.editWorkshop(workshop)}>
          <x-tooltip .text=${html` <x-text> ${this._translation.translate('edit')} </x-text> `}></x-tooltip>
        </tr-button-small>

        <tr-button-small
          icon="delete"
          tone="error"
          .color=${this.colors.tone.error}
          @click=${async () => this.removeWorkshop(workshop)}
        >
          <x-tooltip .text=${html` <x-text> ${this._translation.translate('delete')} </x-text> `}></x-tooltip>
        </tr-button-small>
      </x-flex>
    `;
  }

  timeframeDropdown(field) {
    return html`
      <x-dropdown-selection
        ${field}
        .emptyState=${this._translation.translate('dropdowns', 'emptyStateTimeframes')}
        placeholder=${this._translation.translate('timeframe', 'singular')}
        .selectables=${this.timeframes.map(timeframe => timeframe._id) ?? []}
        .renderString=${(instanceID: string) =>
          this.timeframes.find(timeframe => timeframe._id === instanceID)?.name ?? ''}
      >
      </x-dropdown-selection>
    `;
  }
}
